import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { useOutletContext } from "react-router-dom";

import TagsSection from "../../components/molecules/TagsSection";

import { QueryKeys } from "../../services/types";
import { getAllTags } from "../../services/getAllTags";
import StatusInfo, {
  StatusInfoVariant,
} from "../../components/atoms/StatusInfo";
import {
  ITEMS_PER_PAGE,
  getAllPublishedLinks,
} from "../../services/getAllPublishedLinks";
import SingleLink from "../../components/atoms/SingleLink";
import Button from "../../components/atoms/Button";
import { OutletContextProps } from "../../layouts/Layout";

const HomePage = () => {
  const [, page, setPage, links, setLinks, activeTagId, setActiveTagId] =
    useOutletContext<OutletContextProps>();

  const [isLinksLoading, setIsLinksLoading] = useState(false);
  const [prevActiveTagId, setPrevActiveTagId] = useState<
    number | null | undefined
  >(activeTagId);

  // data.error.message
  const { data } = useQuery({
    queryFn: getAllTags,
    queryKey: [QueryKeys.TAGS],
    refetchOnMount: false,
  });
  const tagList = useMemo(() => data?.data, [data]);

  const {
    data: publishedLinks,
    refetch,
    isLoading,
    isFetching,
  } = useQuery({
    queryFn: () => getAllPublishedLinks(activeTagId, page),
    queryKey: [QueryKeys.ALL_LINKS, page],
    refetchOnMount: false,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (prevActiveTagId !== activeTagId) {
      setIsLinksLoading(true);
      setPage(0);
      setLinks([]);
      refetch().then(() => setIsLinksLoading(false));
    }

    // Update prevActiveTagId after fetching
    setPrevActiveTagId(activeTagId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTagId, prevActiveTagId, refetch]);

  useEffect(() => {
    if (publishedLinks?.data?.length) {
      const newLinks = [...links, ...publishedLinks.data];

      const uniqueIds = new Set();

      const uniqueLinks = newLinks.filter((link) => {
        if (!uniqueIds.has(link.id)) {
          uniqueIds.add(link.id);
          return true;
        }
        return false;
      });

      setLinks(uniqueLinks);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedLinks?.data]);

  const totalPages = Math.ceil(
    Number(publishedLinks?.count) / (ITEMS_PER_PAGE + 1)
  );

  return (
    <StyledHome>
      {tagList?.length ? (
        <TagsSection
          tagList={tagList}
          activeTagId={activeTagId}
          setActiveTagId={setActiveTagId}
          isLoading={isLinksLoading}
        />
      ) : null}

      <StyledPublishedLinks>
        {publishedLinks?.data?.map((link, index) => (
          <SingleLink
            key={`${link.id}_${index}`}
            link={link}
            pageType="home"
            refetchPublishedLinks={refetch}
            tagName={tagList?.filter((tag) => tag.id === link.tag_id)[0].name}
          />
        ))}
      </StyledPublishedLinks>

      <StyledButtonsWrapper>
        {page > 0 && (
          <Button
            text="prev page"
            onClick={() => {
              setPage(page - 1);
            }}
            width="fit-content"
            withHover={false}
          />
        )}

        {page < totalPages - 1 && (
          <Button
            text="next page"
            onClick={() => {
              setPage(page + 1);
            }}
            width="fit-content"
            withHover={false}
          />
        )}
      </StyledButtonsWrapper>

      {(isLinksLoading || isLoading || isFetching) && (
        <StatusInfo variant={StatusInfoVariant.LOADING} text="loading" />
      )}
    </StyledHome>
  );
};

const StyledHome = styled.article``;

const StyledPublishedLinks = styled.div`
  display: flex;
  flex-flow: wrap row;
  justify-content: center;
  align-items: flex-start;
  gap: 30px 20px;
  width: 100%;
  margin: 20px auto 30px;
`;

const StyledButtonsWrapper = styled.div`
  display: flex;
  max-width: 400px;
  margin: 30px auto;
`;

export default HomePage;
